export const DEFAULT_MAX_JSON_RESPONSE_BYTES = 1024 * 1024

export class JsonResponseDecodeError extends Error {
  readonly context: string
  readonly code:
    | "empty"
    | "invalid-content-type"
    | "oversized"
    | "truncated-or-invalid"

  constructor(
    context: string,
    code: JsonResponseDecodeError["code"]
  ) {
    super(`Invalid ${context} response`)
    this.name = "JsonResponseDecodeError"
    this.context = context
    this.code = code
  }
}

type ReadJsonResponseOptions = {
  context?: string
  maxBytes?: number
  optional?: boolean
}

const isJsonContentType = (contentType: string | null): boolean => {
  if (!contentType) return false
  const mediaType = contentType.split(";", 1)[0]?.trim().toLowerCase() || ""
  return mediaType === "application/json" || mediaType.endsWith("+json")
}

const readBoundedBody = async (
  response: Response,
  maxBytes: number,
  context: string
): Promise<Uint8Array> => {
  const declaredLength = Number(response.headers.get("content-length"))
  if (Number.isFinite(declaredLength) && declaredLength > maxBytes) {
    throw new JsonResponseDecodeError(context, "oversized")
  }

  if (!response.body) return new Uint8Array()

  const reader = response.body.getReader()
  const chunks: Uint8Array[] = []
  let total = 0
  try {
    while (true) {
      const { done, value } = await reader.read()
      if (done) break
      if (!value) continue
      total += value.byteLength
      if (total > maxBytes) {
        await reader.cancel()
        throw new JsonResponseDecodeError(context, "oversized")
      }
      chunks.push(value)
    }
  } finally {
    reader.releaseLock()
  }

  const body = new Uint8Array(total)
  let offset = 0
  for (const chunk of chunks) {
    body.set(chunk, offset)
    offset += chunk.byteLength
  }
  return body
}

// Callers that have a response contract should pass T explicitly. The default
// remains compatible with the native Response.json() typing while every body
// still goes through the bounded/content-type-aware decoder above.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const readJsonResponse = async <T = any>(
  response: Response,
  options: ReadJsonResponseOptions = {}
): Promise<T | null> => {
  const context = options.context?.trim() || "API"
  const maxBytes = Math.max(
    1024,
    Math.floor(options.maxBytes || DEFAULT_MAX_JSON_RESPONSE_BYTES)
  )
  const optional = options.optional === true

  if (response.status === 204 || response.status === 205) {
    if (optional) return null
    throw new JsonResponseDecodeError(context, "empty")
  }

  if (!isJsonContentType(response.headers.get("content-type"))) {
    if (optional) return null
    throw new JsonResponseDecodeError(context, "invalid-content-type")
  }

  let bytes: Uint8Array
  try {
    bytes = await readBoundedBody(response, maxBytes, context)
  } catch (error) {
    if (optional && error instanceof JsonResponseDecodeError) return null
    throw error
  }
  if (bytes.byteLength === 0) {
    if (optional) return null
    throw new JsonResponseDecodeError(context, "empty")
  }

  try {
    const text = new TextDecoder("utf-8", { fatal: true }).decode(bytes)
    return JSON.parse(text) as T
  } catch (error) {
    if (error instanceof JsonResponseDecodeError) throw error
    if (optional) return null
    throw new JsonResponseDecodeError(context, "truncated-or-invalid")
  }
}
